/**
 * @file OrderItemsList.tsx
 * @description Карточка состава заказа: строки и подвал с итогом
 * (выделено из `OrderItemsTab` в phase19.24 шаг 5).
 *
 * Вкладка осталась про данные и правки, список — про подачу. Делить пришлось
 * из-за привязки позиции без товара каталога (OQ-54): у вкладки появился свой
 * диалог, и всё вместе не влезало в лимит 200 строк.
 */

import { Package } from "lucide-react";

import { OrderItemEditRow } from "./OrderItemEditRow";
import { useFocusedRow } from "../../hooks/useFocusedRow";
import type { OrderItemRawDto } from "../../lib/api-order-items";
import { formatMoney, type OrderDto } from "../../lib/api-orders";
import { EditorialCard } from "../ui/EditorialCard";

interface Props {
  order: OrderDto;
  items: OrderItemRawDto[];
  /** Режим правки состава (степперы и удаление). */
  editing: boolean;
  busy: boolean;
  /** Право SHOP_MANAGE_ORDERS: показывать ли «Привязать товар». */
  canRelink: boolean;
  /** id позиции из `?tab=items&focus=<id>` — строку обводим. */
  focusId: string | null;
  onQuantity: (item: OrderItemRawDto, quantity: number) => void;
  onRemove: (item: OrderItemRawDto) => void;
  onRelink: (item: OrderItemRawDto) => void;
}

export function OrderItemsList({
  order,
  items,
  editing,
  busy,
  canRelink,
  focusId,
  onQuantity,
  onRemove,
  onRelink,
}: Props) {
  // Та же подсветка, что у платежей и отгрузок: прокрутка к строке плюс
  // обводка на пару секунд — переход из полосы «Сейчас» должен быть заметен.
  const { highlightedId } = useFocusedRow(focusId);

  return (
    <EditorialCard>
      <EditorialCard.Body className="p-0">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-on-surface-variant">
            <Package className="size-8" />
            <p>В заказе нет позиций.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-on-surface/8">
            {items.map((it) => (
              <OrderItemEditRow
                key={it.id}
                item={it}
                editing={editing}
                busy={busy}
                canRelink={canRelink}
                focused={highlightedId === it.id}
                onQuantity={onQuantity}
                onRemove={onRemove}
                onRelink={onRelink}
              />
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-on-surface/8 p-4">
          <span className="text-sm text-on-surface-variant">
            {items.length} поз. {busy ? "· сохраняем…" : ""}
          </span>
          <span className="text-base font-semibold tabular-nums text-primary">
            {formatMoney(order.totalGross)} {order.currency}
          </span>
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
